import { useState, useEffect } from 'react';
import { FiDatabase, FiServer } from 'react-icons/fi';
import { motion } from 'framer-motion';

interface DemoModeToggleProps {
  className?: string;
}

const DemoModeToggle = ({ className = '' }: DemoModeToggleProps) => {
  // Get the initial state from localStorage or default to true (demo mode)
  const [isDemoMode, setIsDemoMode] = useState(() => {
    const savedMode = localStorage.getItem('demoMode');
    return savedMode !== null ? savedMode === 'true' : true;
  });

  // Update localStorage when the mode changes
  useEffect(() => {
    localStorage.setItem('demoMode', isDemoMode.toString());
    // Dispatch a custom event so other components can react to the change
    window.dispatchEvent(new CustomEvent('demoModeChange', { detail: { isDemoMode } }));
  }, [isDemoMode]);

  const toggleDemoMode = () => {
    setIsDemoMode(!isDemoMode);
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="glass-card p-2 shadow-lg"
      >
        <div className="flex items-center">
          <button
            onClick={toggleDemoMode}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            title={isDemoMode ? "Using Demo Data" : "Using API Data"}
          >
            <div className="relative w-10 h-5 bg-dark-700 rounded-full">
              <motion.div
                className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-gradient-to-r"
                animate={{
                  left: isDemoMode ? '0.125rem' : '1.375rem',
                  background: isDemoMode 
                    ? 'linear-gradient(to right, #5a96ff, #7c3aed)' 
                    : 'linear-gradient(to right, #ec4899, #8b5cf6)'
                }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </div>
            <span className="text-white">
              {isDemoMode ? (
                <div className="flex items-center">
                  <FiDatabase className="mr-1" />
                  <span>Demo Mode</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <FiServer className="mr-1" />
                  <span>API Mode</span>
                </div>
              )}
            </span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default DemoModeToggle;